/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  ListLiteLLMInstancesRequest,
  ListLiteLLMInstancesSecurity,
} from "../models/operations/listlitellminstances.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildLiteLLMInstancesQuery,
  LiteLLMInstancesQueryData,
  prefetchLiteLLMInstances,
  queryKeyLiteLLMInstances,
} from "./liteLLMInstances.core.js";
export {
  buildLiteLLMInstancesQuery,
  type LiteLLMInstancesQueryData,
  prefetchLiteLLMInstances,
  queryKeyLiteLLMInstances,
};

export type LiteLLMInstancesQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * listInstances litellm
 *
 * @remarks
 * List active and revoked LiteLLM integrations for a project. Plaintext keys are never returned. Requires org:admin.
 */
export function useLiteLLMInstances(
  request?: ListLiteLLMInstancesRequest | undefined,
  security?: ListLiteLLMInstancesSecurity | undefined,
  options?: QueryHookOptions<
    LiteLLMInstancesQueryData,
    LiteLLMInstancesQueryError
  >,
): UseQueryResult<LiteLLMInstancesQueryData, LiteLLMInstancesQueryError> {
  const client = useGramContext();
  return useQuery({
    ...buildLiteLLMInstancesQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * listInstances litellm
 *
 * @remarks
 * List active and revoked LiteLLM integrations for a project. Plaintext keys are never returned. Requires org:admin.
 */
export function useLiteLLMInstancesSuspense(
  request?: ListLiteLLMInstancesRequest | undefined,
  security?: ListLiteLLMInstancesSecurity | undefined,
  options?: SuspenseQueryHookOptions<
    LiteLLMInstancesQueryData,
    LiteLLMInstancesQueryError
  >,
): UseSuspenseQueryResult<
  LiteLLMInstancesQueryData,
  LiteLLMInstancesQueryError
> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildLiteLLMInstancesQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setLiteLLMInstancesData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      gramSession?: string | undefined;
      gramProject?: string | undefined;
    },
  ],
  data: LiteLLMInstancesQueryData,
): LiteLLMInstancesQueryData | undefined {
  const key = queryKeyLiteLLMInstances(...queryKeyBase);

  return client.setQueryData<LiteLLMInstancesQueryData>(key, data);
}

export function invalidateLiteLLMInstances(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      gramSession?: string | undefined;
      gramProject?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "litellm", "listInstances", ...queryKeyBase],
  });
}

export function invalidateAllLiteLLMInstances(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "litellm", "listInstances"],
  });
}
